import { getApiBaseUrl } from '../config/api';

// FEAT-041 — Admin client for the moderation queue endpoints.
// Uses raw fetch so we can issue PATCH (the shared apiClient exposes
// GET/POST/PUT/DELETE only).

export type ReportStatus = 'OPEN' | 'UNDER_REVIEW' | 'RESOLVED' | 'DISMISSED';
export type ReportTargetType = 'PROFILE' | 'MESSAGE';
export type ReportReasonCode =
  | 'SPAM'
  | 'HARASSMENT'
  | 'UNSAFE_CONTENT'
  | 'MISREPRESENTED_CREDENTIALS'
  | 'OTHER';
export type ReportActionTaken = 'NONE' | 'WARNED' | 'RESTRICTED' | 'SUSPENDED';

export interface ReportRow {
  id: string;
  reporterId: string;
  targetType: ReportTargetType;
  targetId: string;
  targetSnapshot: Record<string, unknown>;
  reasonCode: ReportReasonCode;
  contextText: string;
  status: ReportStatus;
  actionTaken: ReportActionTaken;
  resolutionNotes: string | null;
  resolvedById: string | null;
  resolvedAt: string | null;
  createdAt: string;
  updatedAt: string;
  reporter?: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    email: string;
    role: string;
  } | null;
  resolvedBy?: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    email: string;
  } | null;
}

export interface ReportListResponse {
  reports: ReportRow[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface ReportListFilters {
  status?: ReportStatus;
  targetType?: ReportTargetType;
  reasonCode?: ReportReasonCode;
  reporterId?: string;
  reporteeId?: string;
  page?: number;
  limit?: number;
}

export interface TransitionReportInput {
  status?: ReportStatus;
  actionTaken?: ReportActionTaken;
  resolutionNotes?: string;
}

class ReportAdminService {
  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('authToken');
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  async listReports(filters: ReportListFilters): Promise<ReportListResponse> {
    const baseUrl = await getApiBaseUrl();
    const qs = new URLSearchParams();
    if (filters.status) qs.set('status', filters.status);
    if (filters.targetType) qs.set('targetType', filters.targetType);
    if (filters.reasonCode) qs.set('reasonCode', filters.reasonCode);
    if (filters.reporterId) qs.set('reporterId', filters.reporterId);
    if (filters.reporteeId) qs.set('reporteeId', filters.reporteeId);
    if (filters.page) qs.set('page', String(filters.page));
    if (filters.limit) qs.set('limit', String(filters.limit));
    const query = qs.toString();
    const response = await fetch(
      `${baseUrl}/admin/reports${query ? `?${query}` : ''}`,
      { headers: this.getAuthHeaders() },
    );
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body?.error?.message || 'Failed to load moderation queue');
    }
    return response.json();
  }

  async getReport(id: string): Promise<{ report: ReportRow }> {
    const baseUrl = await getApiBaseUrl();
    const response = await fetch(`${baseUrl}/admin/reports/${id}`, {
      headers: this.getAuthHeaders(),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body?.error?.message || 'Failed to load report');
    }
    return response.json();
  }

  async transitionReport(
    id: string,
    input: TransitionReportInput,
  ): Promise<{ report: ReportRow }> {
    const baseUrl = await getApiBaseUrl();
    const response = await fetch(`${baseUrl}/admin/reports/${id}`, {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(input),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body?.error?.message || 'Failed to update report');
    }
    return response.json();
  }
}

export const reportAdminService = new ReportAdminService();
